import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { MultilineTextModel } from '../primitive/multiline-text.model';
import { TextModel } from '../primitive/text.model';
import { ArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from './crm-code.model';
import { DropdownModel } from './dropdown.model';
import * as _ from 'lodash';

const MAX_CHAR_LENGTH = 40;
const MIN_CHAR_LENGTH = 10;
const ROWS = 3;

export class ConfirmationReasonMissingProtocolModel extends CompositeModel {
    public reasonForMissingProtocols: DropdownModel<TextModel>;
    public reasonSelected: TextModel | undefined;
    public displayOtherReasonTextField = false;
    public otherReasonDescription: MultilineTextModel;

    public constructor(public readonly notificationType: ConfigConstants.NotificationTypes) {
        super('ConfirmationReasonMissingProtocolModel');

        const injector = CustomInjector.getInjector();
        const valueHelperService = injector.get(ValueHelperInstanceToken);

        const valueHelpers: ValueHelperModel[] = valueHelperService.getReasonsMissingProtocolsByCode(
            this.notificationType,
        );

        const entries = this.populateDropdown(valueHelpers);

        // If the array of possible reasons is empty then don't display dropdown nor otherReason input field.
        // Just make the Apply and Open Form visible in the modal
        if (entries.length > 0) {
            this.reasonForMissingProtocols = new DropdownModel<TextModel>('MissingProtocols', entries);
            this.reasonForMissingProtocols.options = {
                ...this.reasonForMissingProtocols.options,
                required: true,
                autoSelect: true,
            };

            this.otherReasonDescription = new MultilineTextModel('OtherReasonDescription');

            this.otherReasonDescription.options = {
                ...this.otherReasonDescription.options,
                required: true,
                rows: ROWS,
                maxLength: MAX_CHAR_LENGTH,
                minLength: MIN_CHAR_LENGTH,
            };
        }
    }

    public populateDropdown(valueHelpers: ValueHelperModel[]): Array<DisplayDecorator<TextModel>> {
        const options: Array<DisplayDecorator<TextModel>> = [];
        valueHelpers.forEach((valueHelper) => {
            const textModel = new TextModel(valueHelper.descText, valueHelper.code);
            const decorator = new DisplayDecorator<TextModel>(valueHelper.descText, textModel);

            options.push(decorator);
        });

        return options;
    }

    public onReasonSelected(): void {
        this.reasonSelected = this.reasonForMissingProtocols.selectedEntry.model;
        this.displayOtherReasonTextField =
            this.reasonSelected.value === ConfigConstants.CrmMissingProtocolReasonCodes.OTHR;
    }

    /**
     * Re-selects a documented reason through the dropdown, so `getOutModel()` reads the entry's
     * current label exactly as it does after the technician picks it in the modal.
     *
     * @param reasonCode the stored reason code
     * @param otherReasonDescription the stored free text, applied only for the `OTHR` reason
     * @returns false when the code is no longer among the offered reasons, which leaves the reason
     * unselected so the modal asks for one again
     */
    public restoreReason(reasonCode: string, otherReasonDescription: string): boolean {
        if (!this.reasonForMissingProtocols?.selectByValue(reasonCode)) {
            return false;
        }
        this.onReasonSelected();
        if (this.displayOtherReasonTextField) {
            this.otherReasonDescription.value = otherReasonDescription;
        }
        return true;
    }

    /**
     * Only the selection is business data; the options are rebuilt from the value helpers on every
     * load. Returning `undefined` while nothing is selected leaves the key out of the stored JSON.
     *
     * @returns the documented reason, or `undefined` when none is selected
     */
    public toJSON(): JsonConfirmationReasonMissingProtocolModel | undefined {
        if (!this.reasonSelected) {
            return undefined;
        }
        return {
            reasonCode: this.reasonSelected.value,
            otherReasonDescription: this.otherReasonDescription?.value ?? '',
        };
    }

    public getOutModel(): ODataConfirmationReasonMissingProtocolsOutModel {
        const outModel = <ODataConfirmationReasonMissingProtocolsOutModel>super.getOutModel();
        const crmCodes = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet'));
        if (this.reasonSelected) {
            crmCodes.addModel(
                new CrmCodeModel({
                    codeType: ConfigConstants.confirmationMissingProtocolCodeType,
                    codeGroup: ConfigConstants.confirmationMissingProtocolCodeGroup,
                    code: this.reasonSelected.value,
                    codeText:
                        this.reasonSelected.value === ConfigConstants.CrmMissingProtocolReasonCodes.OTHR
                            ? this.otherReasonDescription.value
                            : this.reasonForMissingProtocols.selectedEntry.model.name,
                }),
            );
        }

        _.merge(outModel, crmCodes.getOutModel());
        return outModel;
    }
}

export interface ODataConfirmationReasonMissingProtocolsOutModel extends ODataObject {
    CrmCodesSet: ODataCrmCodeOutModel[];
}

export interface JsonConfirmationReasonMissingProtocolModel {
    reasonCode: string;
    otherReasonDescription: string;
}
